import AppText from '@/entities/AppText';
import Language from '@/entities/Language';
import Project, {
  addLanguageToProject,
  deleteProjectLanguage,
  updateProjectMainLanguage,
} from '@/entities/Project';
import { getRemoteProjectByAppId } from '@/firebase/RemoteProject';
import React, { createContext, useContext, useState } from 'react';

interface ProjectContextProps {
  project: Project | null;
  initializeProject: (appId: string) => Promise<void>;
  setProject: (project: Project) => void;
  deleteLanguage: (deletedLanguage: Language) => void;
  updateMainLanguage: (mainLanguage: Language) => void;
  addLanguage: (language: Language) => void;
}

const ProjectContext = createContext<ProjectContextProps | undefined>(
  undefined,
);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [project, setProject] = useState<Project | null>(null);
  const [appTexts, setAppTexts] = useState<AppText[]>([]);

  const initializeProject = async (appId: string) => {
    try {
      if (appId) {
        const fetchedProject = await getRemoteProjectByAppId(appId);
        if (fetchedProject) {
          setProject(fetchedProject);
        } else {
          console.warn('No project found, setting to NEW_PROJECT view');
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation du projet :", error);
    }
  };

  const deleteLanguage = (deletedLanguage: Language) => {
    if (!project) return;
    const updatedProject = deleteProjectLanguage(project, deletedLanguage);
    setProject(updatedProject);
  };

  const updateMainLanguage = (mainLanguage: Language) => {
    if (!project) return;
    const updatedProject = updateProjectMainLanguage(project, mainLanguage);
    setProject(updatedProject);
  };

  const addLanguage = (language: Language) => {
    if (!project) return;
    const updatedProject = addLanguageToProject(project, language);
    setProject(updatedProject);
  };

  return (
    <ProjectContext.Provider
      value={{
        project,
        setProject,
        initializeProject,
        deleteLanguage,
        updateMainLanguage,
        addLanguage,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextProps => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
